/**
 * Vector Clock Visualization Component
 * 
 * Simple, intuitive visualization of vector clocks showing logical time
 * progression across nodes in an easy-to-understand timeline format.
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Activity, AlertTriangle, CheckCircle, Circle } from 'lucide-react';
import type { VectorClock, Node } from '../../types'

export interface VectorClockData {
  nodeId: string
  nodeName: string
  vectorClock: VectorClock
  timestamp: Date
  isActive: boolean
  status: 'online' | 'offline' | 'degraded'
}

interface VectorClockVisualizationProps {
  nodes: Node[]
  vectorClocks: VectorClockData[]
  onNodeSelect?: (nodeId: string) => void
  className?: string
}

export const VectorClockVisualization: React.FC<VectorClockVisualizationProps> = ({
  nodes,
  vectorClocks,
  onNodeSelect,
  className = '',
}) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  // Calculate simplified clock metrics
  const clockMetrics = useMemo(() => {
    const maxClockValue = Math.max(...vectorClocks.flatMap(v => Object.values(v.vectorClock)));
    const minClockValue = Math.min(...vectorClocks.flatMap(v => Object.values(v.vectorClock)));
    
    // Calculate sync status - nodes are "in sync" if their clock values are close
    const avgClockValue = vectorClocks.reduce((sum, v) => {
      const nodeClockValue = Object.values(v.vectorClock).reduce((a, b) => a + b, 0);
      return sum + nodeClockValue;
    }, 0) / vectorClocks.length;

    const syncThreshold = maxClockValue * 0.1; // 10% tolerance
    const inSyncNodes = vectorClocks.filter(v => {
      const nodeClockValue = Object.values(v.vectorClock).reduce((a, b) => a + b, 0);
      return Math.abs(nodeClockValue - avgClockValue) <= syncThreshold;
    }).length;

    return {
      maxClockValue,
      minClockValue,
      avgClockValue,
      clockDrift: maxClockValue - minClockValue,
      syncPercentage: (inSyncNodes / vectorClocks.length) * 100,
      totalEvents: vectorClocks.reduce((sum, v) => sum + Object.values(v.vectorClock).reduce((a, b) => a + b, 0), 0),
    };
  }, [vectorClocks]);

  // Sort nodes by their total clock value for easy comparison
  const sortedClocks = useMemo(() => {
    return [...vectorClocks].sort((a, b) => {
      const aTotal = Object.values(a.vectorClock).reduce((sum, val) => sum + val, 0);
      const bTotal = Object.values(b.vectorClock).reduce((sum, val) => sum + val, 0);
      return bTotal - aTotal;
    });
  }, [vectorClocks]);

  const getNodeColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'offline': return 'bg-red-500';
      case 'degraded': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getClockValueColor = (value: number) => {
    const intensity = Math.min(value / clockMetrics.maxClockValue, 1);
    if (intensity > 0.8) {
return 'bg-blue-500';
}
    if (intensity > 0.6) {
return 'bg-blue-400';
}
    if (intensity > 0.4) {
return 'bg-blue-300';
}
    if (intensity > 0.2) {
return 'bg-blue-200';
}
    return 'bg-gray-300';
  };

  // With no readings the metrics above have nothing to measure (Math.max of
  // nothing is -Infinity, the average NaN), so the timeline says so instead.
  if (vectorClocks.length === 0) {
    return (
      <div className={`bg-console-card rounded-lg border border-console-border ${className}`}>
        <div className="flex items-center space-x-2 p-4 border-b border-console-border">
          <Clock className="w-5 h-5 text-console-accent" />
          <h3 className="text-lg font-semibold text-console-text">Vector Clock Timeline</h3>
        </div>
        <div className="p-8 text-center text-sm text-console-muted">
          No vector clock data available
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-console-card rounded-lg border border-console-border ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-console-border">
        <div className="flex items-center space-x-2">
          <Clock className="w-5 h-5 text-console-accent" />
          <h3 className="text-lg font-semibold text-console-text">Vector Clock Timeline</h3>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Sync Status */}
          <div className="flex items-center space-x-2">
            {clockMetrics.syncPercentage > 80 ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <Circle className="w-4 h-4 text-yellow-500" />
            )}
            <span className="text-sm text-console-muted">
              {Math.round(clockMetrics.syncPercentage)}% synchronized
            </span>
          </div>
          
          {/* Active Nodes Count */}
          <div className="flex items-center space-x-1">
            <Activity className="w-4 h-4 text-console-accent" />
            <span className="text-sm text-console-muted">
              {vectorClocks.filter(v => v.isActive).length}/{vectorClocks.length} active
            </span>
          </div>
        </div>
      </div>

      {/* Clock Drift Alert */}
      <AnimatePresence>
        {clockMetrics.clockDrift > clockMetrics.maxClockValue * 0.3 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mx-4 mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg"
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-300">
                High clock drift detected (variance: {clockMetrics.clockDrift} events)
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Stats */}
      <div className="p-4 border-b border-console-border bg-console-bg/30">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-console-accent">{clockMetrics.totalEvents}</div>
            <div className="text-xs text-console-muted">Total Events</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-green-400">{clockMetrics.maxClockValue}</div>
            <div className="text-xs text-console-muted">Highest Clock</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-blue-400">{Math.round(clockMetrics.avgClockValue)}</div>
            <div className="text-xs text-console-muted">Average Clock</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-orange-400">{clockMetrics.clockDrift}</div>
            <div className="text-xs text-console-muted">Clock Drift</div>
          </div>
        </div>
      </div>

      {/* Node Timeline View */}
      <div className="p-4">
        <div className="space-y-3">
          {sortedClocks.map((clockData, index) => {
            const nodeData = nodes.find(n => n.id === clockData.nodeId);
            const totalClockValue = Object.values(clockData.vectorClock).reduce((sum, val) => sum + val, 0);
            const isSelected = selectedNode === clockData.nodeId;
            const toggleSelection = () => {
              setSelectedNode(isSelected ? null : clockData.nodeId);
              onNodeSelect?.(clockData.nodeId);
            };
            
            return (
              <motion.div
                key={clockData.nodeId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`p-3 rounded-lg border transition-all cursor-pointer ${
                  isSelected 
                    ? 'border-console-accent bg-console-accent/10' 
                    : 'border-console-border hover:border-console-accent/50 hover:bg-console-bg/50'
                }`}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                onClick={toggleSelection}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleSelection();
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    {/* Node Status Indicator */}
                    <div className={`w-3 h-3 rounded-full ${getNodeColor(clockData.status)}`} />
                    
                    {/* Node Info */}
                    <div>
                      <div className="font-medium text-console-text">
                        {nodeData?.name || clockData.nodeName}
                      </div>
                      <div className="text-xs text-console-muted">
                        {clockData.nodeId} • Total: {totalClockValue} events
                      </div>
                    </div>
                    
                    {/* Active Indicator */}
                    {clockData.isActive && (
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        <span className="ml-1 text-xs text-green-400">Active</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Clock Value */}
                  <div className="text-right">
                    <div className="text-lg font-mono font-semibold text-console-accent">
                      {totalClockValue}
                    </div>
                    <div className="text-xs text-console-muted">
                      Updated {clockData.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>

                {/* Individual Clock Values */}
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-console-muted mr-2">Clock values:</span>
                  {Object.entries(clockData.vectorClock)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([nodeId, value]) => (
                      <div
                        key={nodeId}
                        className="flex items-center space-x-1 px-2 py-1 rounded bg-console-bg border border-console-border/50"
                      >
                        <div className={`w-2 h-2 rounded-full ${getClockValueColor(value)}`} />
                        <span className="text-xs font-mono text-console-text">
                          {nodeId.substring(0, 4)}:{value}
                        </span>
                      </div>
                    ))}
                </div>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 pt-3 border-t border-console-border/50"
                    >
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-console-muted mb-1">Synchronization</div>
                          <div className="text-console-text">
                            {Math.abs(totalClockValue - clockMetrics.avgClockValue) <= clockMetrics.maxClockValue * 0.1 
                              ? '✓ In Sync' 
                              : '⚠ Drifted'}
                          </div>
                        </div>
                        <div>
                          <div className="text-console-muted mb-1">Last Activity</div>
                          <div className="text-console-text">
                            {clockData.timestamp.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div className="text-console-muted mb-1">Node Role</div>
                          <div className="text-console-text">
                            {(nodeData as any)?.role || 'Unknown'}
                          </div>
                        </div>
                        <div>
                          <div className="text-console-muted mb-1">Status</div>
                          <div className={`capitalize ${
                            clockData.status === 'online' ? 'text-green-400' :
                            clockData.status === 'degraded' ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {clockData.status}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-console-border bg-console-bg/50">
        <div className="text-xs text-console-muted mb-2">Understanding Vector Clocks:</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full" />
            <span className="text-console-muted">High activity (recent events)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-gray-300 rounded-full" />
            <span className="text-console-muted">Low activity (older events)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-console-muted">Currently active node</span>
          </div>
        </div>
        <div className="mt-2 text-xs text-console-muted">
          Vector clocks track the logical ordering of events across distributed nodes. 
          Higher numbers indicate more recent activity. Nodes should stay synchronized for optimal performance.
        </div>
      </div>
    </div>
  );
};